import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Sparkles, CheckSquare, Gift, ExternalLink, Loader2, Wand2 } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  GiftSuggestionForm,
  GiftFormData,
  formDataToApiPayload,
} from './GiftSuggestionForm';

interface AIGiftSuggestionsProps {
  onNavigate: (screen: string, data?: { giftGroupId?: string; tab?: string }) => void;
  giftGroupId: string | null;
  embedded?: boolean;
  onSuggestionsAdded?: () => void;
}

const descriptorTags = [
  'Teen', 'Young Adult', 'Parent', 'Grandparent',
  'Workmate', 'Boss', 'Customer', 'Client', 'Alumni',
  'Runner', 'Hiker', 'Boater', 'Camper', 'Gamer',
  'Foodie', 'Rocker', 'Classical', 'Artist', 'Reader',
  'Tech Enthusiast', 'Fitness', 'Yoga', 'Golfer', 'Traveler',
  'Crafter', 'Gardener', 'Pet Lover', 'Sports Fan', 'Musician'
];

export function AIGiftSuggestions({ onNavigate, giftGroupId, embedded = false, onSuggestionsAdded }: AIGiftSuggestionsProps) {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedGifts, setSelectedGifts] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  
  // New state for verbose prompt
  const [freeTextPrompt, setFreeTextPrompt] = useState('');
  const [budget, setBudget] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [lastPromptUsed, setLastPromptUsed] = useState<string>('');
  
  // State for edit/approve flow
  const [editingQueue, setEditingQueue] = useState<any[]>([]);
  const [currentEditIndex, setCurrentEditIndex] = useState(0);
  const [editingGift, setEditingGift] = useState<GiftFormData | null>(null);

  // Mark AI suggestions as viewed when component mounts
  useEffect(() => {
    const markAiViewed = async () => {
      if (!giftGroupId) return;
      
      try {
        await api.markAIViewed(giftGroupId);
        console.log('Marked AI suggestions as viewed');
      } catch (error) {
        // Silently log error - this is a background analytics action that shouldn't disrupt user experience
        console.error('Failed to mark AI viewed:', error);
      }
    };
    
    markAiViewed();
  }, [giftGroupId]);

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const toggleGift = (id: number) => {
    setSelectedGifts(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedGifts([]);
    } else {
      setSelectedGifts(aiSuggestions.map((_, index) => index));
    }
    setSelectAll(!selectAll);
  };

  // Fetch AI suggestions - callable on demand
  const fetchSuggestions = useCallback(async () => {
    if (!giftGroupId) return;
    
    // Need either tags or a free-text prompt
    if (selectedTags.length === 0 && !freeTextPrompt.trim()) {
      toast.error('Please select some tags or describe what you\'re looking for');
      return;
    }
    
    try {
      setLoading(true);
      setHasSearched(true);
      
      // Build a summary of what we're searching for
      const searchSummary = [
        freeTextPrompt.trim(),
        selectedTags.length > 0 ? `Tags: ${selectedTags.join(', ')}` : '',
        budget ? `Budget: $${budget}` : ''
      ].filter(Boolean).join(' | ');
      setLastPromptUsed(searchSummary);
      
      const budgetValue = budget ? parseFloat(budget) : undefined;
      const response = await api.getAISuggestions(
        giftGroupId, 
        selectedTags, 
        budgetValue,
        freeTextPrompt.trim() || undefined
      );
      
      setAiSuggestions(response.suggestions || []);
      setSelectedGifts([]); // Reset selection when new results come in
      setSelectAll(false);
      
      if (response.isUsingFallback) {
        toast.info('Using sample suggestions. Connect OpenAI for personalized results.');
      } else if (response.suggestions?.length > 0) {
        toast.success(`Found ${response.suggestions.length} gift ideas!`);
      } else {
        toast.info('No suggestions found. Try different descriptors.');
      }
    } catch (error) {
      console.error('Failed to get AI suggestions:', error);
      toast.error('Failed to get AI suggestions');
    } finally {
      setLoading(false);
    }
  }, [giftGroupId, selectedTags, freeTextPrompt, budget]);

  // Start the edit/approve flow - queue up selected suggestions for review
  const handleAddSuggestions = () => {
    if (!giftGroupId) {
      toast.error('No gift group selected');
      return;
    }

    const selectedSuggestions = aiSuggestions.filter((_, index) => selectedGifts.includes(index));
    
    if (selectedSuggestions.length === 0) {
      toast.error('Please select at least one suggestion');
      return;
    }

    // Queue up the suggestions for editing
    setEditingQueue(selectedSuggestions);
    setCurrentEditIndex(0);
    
    // Start editing the first suggestion
    const firstSuggestion = selectedSuggestions[0];
    setEditingGift({
      url: firstSuggestion.url || '',
      name: firstSuggestion.name || '',
      description: firstSuggestion.description || '',
      price: firstSuggestion.price != null ? String(firstSuggestion.price) : '',
      imageUrl: firstSuggestion.imageUrl || '',
      shippingCost: firstSuggestion.shippingCost != null ? String(firstSuggestion.shippingCost) : '',
      estimatedDeliveryDays:
        firstSuggestion.estimatedDeliveryDays != null
          ? String(firstSuggestion.estimatedDeliveryDays)
          : '',
      suggestedOrderDate: firstSuggestion.suggestedOrderDate || '',
    });
  };

  // Save the current edited suggestion and move to next (or finish)
  const handleSaveAndNext = async () => {
    if (!giftGroupId || !editingGift) return;
    
    try {
      setAdding(true);
      
      // Add the current edited suggestion using shared helper
      const payload = formDataToApiPayload(editingGift, 'ai');
      await api.addGiftSuggestion(giftGroupId, payload);

      const nextIndex = currentEditIndex + 1;
      
      if (nextIndex < editingQueue.length) {
        // Move to the next suggestion
        setCurrentEditIndex(nextIndex);
        const nextSuggestion = editingQueue[nextIndex];
        setEditingGift({
          url: nextSuggestion.url || '',
          name: nextSuggestion.name || '',
          description: nextSuggestion.description || '',
          price: nextSuggestion.price != null ? String(nextSuggestion.price) : '',
          imageUrl: nextSuggestion.imageUrl || '',
          shippingCost: nextSuggestion.shippingCost != null ? String(nextSuggestion.shippingCost) : '',
          estimatedDeliveryDays:
            nextSuggestion.estimatedDeliveryDays != null
              ? String(nextSuggestion.estimatedDeliveryDays)
              : '',
          suggestedOrderDate: nextSuggestion.suggestedOrderDate || '',
        });
        toast.success(`Saved suggestion ${currentEditIndex + 1} of ${editingQueue.length}`);
      } else {
        // All done
        toast.success(`Added all ${editingQueue.length} suggestion${editingQueue.length !== 1 ? 's' : ''}!`);
        setEditingQueue([]);
        setEditingGift(null);
        setCurrentEditIndex(0);
        setSelectedGifts([]);
        setSelectAll(false);
        
        // Navigate or callback
        if (embedded && onSuggestionsAdded) {
          onSuggestionsAdded();
        } else {
          onNavigate('gift-suggestions', { giftGroupId });
        }
      }
    } catch (error) {
      console.error('Failed to add suggestion:', error);
      toast.error('Failed to save suggestion');
    } finally {
      setAdding(false);
    }
  };

  // Skip the current suggestion and move to next
  const handleSkip = () => {
    const nextIndex = currentEditIndex + 1;
    
    if (nextIndex < editingQueue.length) {
      setCurrentEditIndex(nextIndex);
      const nextSuggestion = editingQueue[nextIndex];
      setEditingGift({
        url: nextSuggestion.url || '',
        name: nextSuggestion.name || '',
        description: nextSuggestion.description || '',
        price: nextSuggestion.price != null ? String(nextSuggestion.price) : '',
        imageUrl: nextSuggestion.imageUrl || '',
        shippingCost: nextSuggestion.shippingCost != null ? String(nextSuggestion.shippingCost) : '',
        estimatedDeliveryDays:
          nextSuggestion.estimatedDeliveryDays != null
            ? String(nextSuggestion.estimatedDeliveryDays)
            : '',
        suggestedOrderDate: nextSuggestion.suggestedOrderDate || '',
      });
    } else {
      // All done (all skipped or processed)
      toast.info('Finished reviewing suggestions');
      setEditingQueue([]);
      setEditingGift(null);
      setCurrentEditIndex(0);
    }
  };

  // Cancel the entire edit flow
  const handleCancelEdit = () => {
    setEditingQueue([]);
    setEditingGift(null);
    setCurrentEditIndex(0);
  };

  const handleAddOwn = () => {
    if (giftGroupId) {
      onNavigate('gift-suggestions', { giftGroupId });
    }
  };

  // If we're in edit mode, show the edit form
  if (editingGift) {
    const isLastInQueue = currentEditIndex >= editingQueue.length - 1;
    
    return (
      <div className="min-h-screen bg-background p-4 md:p-8">
        <div className="max-w-2xl mx-auto">
          <GiftSuggestionForm
            data={editingGift}
            onChange={setEditingGift}
            onSubmit={handleSaveAndNext}
            onCancel={handleCancelEdit}
            isSubmitting={adding}
            submitLabel={isLastInQueue ? 'Save & Finish' : 'Save & Next'}
            cancelLabel="Cancel"
            isAISuggestion={true}
            header={{
              title: 'Review & Edit Gift Suggestion',
              subtitle: `Suggestion ${currentEditIndex + 1} of ${editingQueue.length}`,
            }}
            additionalContent={
              editingQueue.length > 1 ? (
                <div className="mt-4 pt-4 border-t">
                  <Button
                    onClick={handleSkip}
                    variant="outline"
                    disabled={adding}
                    className="w-full"
                  >
                    Skip This Suggestion
                  </Button>
                </div>
              ) : null
            }
          />
        </div>
      </div>
    );
  }

  if (!giftGroupId) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="p-6 text-center">
          <h2 className="text-xl mb-2">No Gift Group Selected</h2>
          <p className="text-muted-foreground mb-4">Please select a gift group first</p>
          <Button onClick={() => onNavigate('dashboard', { tab: 'todo' })}>Go to Dashboard</Button>
        </Card>
      </div>
    );
  }

  const content = (
    <>
      {!embedded && (
        <Card className="p-6 border-2 border-primary/20 mb-6 shadow-lg">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Sparkles className="w-6 h-6 text-purple-600" />
            </div>
            <h1 className="text-primary">AI Gift Suggestions</h1>
          </div>
          <p className="text-muted-foreground">Describe who you're shopping for and let AI find the perfect gifts</p>
        </Card>
      )}

        {/* AI Search - Combined Prompt and Tags */}
        <Card className="p-4 border-2 border-primary/20 mb-6 shadow-lg">
          <div className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl text-primary">Describe the Gift Recipient</h2>
          </div>
          <p className="text-sm text-muted-foreground leading-none -my-4">
            Tell us about who you're shopping for - their interests, age, hobbies, or anything else that might help find the perfect gift.
          </p>
          <div className="space-y-1 -mt-1">
            {/* Budget input - inline label and input */}
            <div className="flex items-center gap-3 mt-1">
              <Label htmlFor="budget" className="text-sm font-medium whitespace-nowrap">
                Budget (optional)
              </Label>
              <div className="flex items-center gap-1">
                <span className="text-muted-foreground text-sm">$</span>
                <Input
                  id="budget"
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*\.?[0-9]*"
                  placeholder=""
                  value={budget}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBudget(e.target.value)}
                  className="w-20 !h-5 !py-0 px-2 text-sm leading-none"
                />
              </div>
            </div>
            
            {/* Free-text prompt */}
            <div>
              <Label htmlFor="gift-prompt" className="text-sm font-medium mb-1 block">
                Who is this gift for?
              </Label>
              <Textarea
                id="gift-prompt"
                placeholder="Example: A 16-year-old girl who loves Taylor Swift and is on the cheer team. She also enjoys baking and making TikTok videos..."
                value={freeTextPrompt}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFreeTextPrompt(e.target.value)}
                className="min-h-[100px]"
              />
            </div>
            
            {/* Quick Tags */}
            <div>
              <div className="flex items-baseline gap-2 mb-2">
                <Label className="text-sm font-medium">Quick Tags (optional)</Label>
                <span className="text-sm text-muted-foreground">— Click any tags that describe the recipient to help refine suggestions.</span>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {descriptorTags.map((tag) => (
                  <Badge
                    key={tag}
                    variant={selectedTags.includes(tag) ? 'default' : 'outline'}
                    className="cursor-pointer text-sm py-0 px-2 leading-tight transition-colors hover:bg-primary/20"
                    onClick={() => toggleTag(tag)}
                  >
                    {tag}
                  </Badge>
                ))}
              </div>

              {selectedTags.length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 border-2 border-blue-200 rounded-lg">
                  <div className="text-sm">
                    <strong>Selected:</strong> {selectedTags.join(', ')}
                  </div>
                </div>
              )}
            </div>
            
            {/* Generate button */}
            <Button
              onClick={fetchSuggestions}
              size="lg"
              className="w-full bg-purple-600 hover:bg-purple-700"
              disabled={loading || (!freeTextPrompt.trim() && selectedTags.length === 0)}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Finding Gift Ideas...
                </>
              ) : (
                <>
                  <Wand2 className="w-5 h-5 mr-2" />
                  Generate Gift Suggestions
                </>
              )}
            </Button>
          </div>
        </Card>

        {/* AI Generated Suggestions */}
        <Card className="border-2 border-primary/20 shadow-lg">
          <div className="p-6 border-b-2 border-primary/20 flex items-center justify-between">
            <div>
              <h2 className="text-xl mb-1 text-primary">AI-Powered Gift Suggestions</h2>
              <p className="text-sm text-muted-foreground">
                {hasSearched && aiSuggestions.length > 0
                  ? `${aiSuggestions.length} suggestions found • Click any to add to your gift list`
                  : hasSearched 
                    ? 'No results. Try different descriptors or prompt.'
                    : 'Describe the recipient above and click "Generate" to get personalized gift ideas'
                }
              </p>
              {hasSearched && lastPromptUsed && aiSuggestions.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1 italic">
                  Based on: {lastPromptUsed}
                </p>
              )}
            </div>
            {aiSuggestions.length > 0 && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="select-all"
                  checked={selectAll}
                  onCheckedChange={handleSelectAll}
                />
                <Label htmlFor="select-all" className="cursor-pointer">
                  Select All
                </Label>
              </div>
            )}
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="w-12 h-12 mx-auto mb-4 text-purple-600 animate-spin" />
              <p className="text-muted-foreground">AI is searching for the perfect gifts...</p>
              <p className="text-xs text-muted-foreground mt-2">This may take a few seconds</p>
            </div>
          ) : aiSuggestions.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>{hasSearched 
                ? 'No suggestions found. Try different descriptors or a more detailed prompt.' 
                : 'Describe the gift recipient above to get AI-generated suggestions'
              }</p>
              <p className="text-sm mt-2">Each suggestion will link to a real product you can purchase</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Gift Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {aiSuggestions.map((gift, index) => (
                  <TableRow key={index} className="hover:bg-muted/50">
                    <TableCell>
                      <Checkbox
                        checked={selectedGifts.includes(index)}
                        onCheckedChange={() => toggleGift(index)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Gift className="w-4 h-4 text-muted-foreground" />
                        <strong>{gift.name}</strong>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {gift.description}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">${gift.price.toFixed(2)}</Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" asChild>
                        <a href={gift.url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        {!embedded && (
          <div className="mt-6 space-y-4">
            <div className="flex gap-4">
              <Button
                onClick={handleAddSuggestions}
                size="lg"
                className="flex-1 bg-primary hover:bg-primary/90"
                disabled={selectedGifts.length === 0 || adding}
              >
                {adding ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <CheckSquare className="w-5 h-5 mr-2" />
                    Add {selectedGifts.length} Selected Suggestion{selectedGifts.length !== 1 ? 's' : ''} to List
                  </>
                )}
              </Button>
              <Button
                onClick={handleAddOwn}
                size="lg"
                variant="outline"
                className="flex-1 border-primary/30"
              >
                Skip & Add My Own Suggestions
              </Button>
            </div>

            <div className="flex justify-between items-center flex-wrap gap-4">
              <div className="flex gap-2 flex-wrap">
                <Button 
                  variant="outline" 
                  onClick={() => onNavigate('gift-suggestions', { giftGroupId })}
                >
                  Add Manual Suggestions
                </Button>
                <Button 
                  className="bg-primary hover:bg-primary/90"
                  onClick={() => onNavigate('gift-group-overview', { giftGroupId })}
                >
                  Go to Rankings
                </Button>
              </div>
            </div>
          </div>
        )}

        {embedded && (
          <div className="mt-6">
            <Button
              onClick={handleAddSuggestions}
              size="lg"
              className="w-full bg-primary hover:bg-primary/90"
              disabled={selectedGifts.length === 0 || adding}
            >
              {adding ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <CheckSquare className="w-5 h-5 mr-2" />
                  Add {selectedGifts.length} Selected Suggestion{selectedGifts.length !== 1 ? 's' : ''} to List
                </>
              )}
            </Button>
          </div>
        )}
      </>
  );
  
  if (embedded) {
    return content;
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-6xl mx-auto">
        {content}
      </div>
    </div>
  );
}